import { startOfWeek } from 'date-fns'
import { connect } from 'react-redux'
import { getNestedProperty } from '@opallabs/lambda'
import { actions, selectors, ViewConstants } from '@opallabs/wormhole'
import {
  getContentByIdQueryParams,
  getContentQueryParams,
  getMomentsQueryParams,
  getLabelSetsQueryParams,
} from './queryHelpers'
import { WeekView } from '.'

const mapStateToProps = (state, ownProps) => ({
  content: selectors.selectContent(state),
  // Content-first filtered slice, consumed by `ContentMapProvider` in
  // Content mode. Moment mode ignores it — the nested posts rendered
  // under moment cards still come from `moment.posts[]`.
  filteredContent: selectors.selectFilteredContent(state),
  currentBrandId: state.current_workspace_id || ownProps.brandId,
  focusedDay: selectors.selectWeekViewStartDay(state) || ownProps.focusedDay,
  // The monolith's moment and content shrouds set this; it silences the week's
  // arrow keys.
  isShroudVisible: Boolean(state.isShroudVisible),
  labelSets: selectors.selectV1LabelSets(state),
  momentContentIds: selectors.selectMomentContentIds(state),
  currentError: getNestedProperty(state, 'errors', 'weekView', 0),
  currentWeekChannels: selectors.selectCurrentWeekChannels(state),
  currentWeekLabelSets: selectors.selectCurrentWeekLabelSets(state),
  moments: selectors.selectFilteredMoments(state),
  preConstraint: selectors.selectPreConstraint(state),
  primaryLabelSetId: selectors.selectPrimaryLabelSetId(state),
  width: selectors.selectWeekViewWidth(state),
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  addViewToStore: () =>
    dispatch(
      actions.addViewToStore({
        view: ViewConstants.weekView,
        clearErrors: true,
      })
    ),
  createMoment: (params) =>
    dispatch(
      actions.createMoment({
        callback: params.callback,
        moment: params.moment,
        promise: params.promise,
        relationships: {
          stories: [params.story],
        },
      })
    ),
  deleteContent: (contentId, momentId) =>
    dispatch(actions.destroyContent(contentId, momentId)),
  deleteMoment: (id) => dispatch(actions.destroyMoment(id)),
  // Returns the dispatch promise (resolving to the page's item count) so the
  // WeekView paging loop can await each page and decide whether to fetch more.
  getContent: (offset, startDate, brandId, searchString, includeAssets) => {
    const params = {
      filter: {
        brand_id: brandId,
        story_id: ownProps.storyId,
        scheduled_start_at: startDate,
        use_moment_schedule: true,
        // Matches the moments fetch: an active search term scopes the
        // content slice so Content mode's grid respects Search View.
        search_terms: searchString,
      },
      page: {
        offset,
      },
      omit_count: true,
      includeAssets,
    }
    return dispatch(actions.getContent({ ...getContentQueryParams(params) }))
  },
  getContentById: (ids, brandId, includeAssets) =>
    dispatch(
      actions.getContent(
        getContentByIdQueryParams({
          ids,
          brandId,
          storyId: ownProps.storyId,
          includeAssets,
        })
      )
    ),
  getLabelSets: () =>
    dispatch(
      actions.getLabelSets(
        getLabelSetsQueryParams({
          brandId: ownProps.brandId,
          scopeToCurrentBrand: true,
        })
      )
    ),
  getMoments: ({
    focusedDay,
    searchString,
    startMomentFetch,
    endMomentFetch,
    currentBrandId,
  }) =>
    dispatch(
      actions.getMomentsRequest({
        ...getMomentsQueryParams({
          brandId: currentBrandId,
          scopeToCurrentBrand: true,
          searchString,
          startDate: startOfWeek(focusedDay, {
            weekStartsOn: ownProps.weekStartsOn,
          }),
          storyId: ownProps.storyId,
        }),
        startMomentFetch,
        endMomentFetch,
      })
    ),
  handleError: (params) => dispatch(actions.handleError(params)),
  loadUpdatedJsonApiEntities: (jsonApiDoc) =>
    dispatch(actions.loadUpdatedJsonApiEntities(jsonApiDoc)),
  navigateTo: (params) => dispatch(actions.navigateTo(params)),
  onDismissError: (params) => dispatch(actions.clearError(params)),
  removeViewFromStore: () =>
    dispatch(actions.removeViewFromStore({ view: ViewConstants.weekView })),
  removeJsonApiEntityRelationship: (params) =>
    dispatch(actions.removeJsonApiEntityRelationship(params)),
  requestPrivacyGroupAccess: (params) =>
    dispatch(actions.requestPrivacyGroupAccess(params)),
  setSearchTerm: (searchString) =>
    dispatch(actions.setSearchTerm(searchString)),
  updateContent: (params) => dispatch(actions.updateContentV2(params)),
  updateMoment: (params) => dispatch(actions.updateMoment(params)),
  uploadAsset: (params) => dispatch(actions.handleAssetUploadV2(params)),
})

export const ConnectedWeekView = connect(
  mapStateToProps,
  mapDispatchToProps
)(WeekView)
